import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { signInPath } from '../utils/authNavigation';
import { Activity, LogOut, User, Plus, TrendingUp, Settings, HelpCircle, Menu, X, Flame, Vote } from 'lucide-react';

export const Navbar = () => {
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const location = useLocation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const loginDestination = signInPath(['/login', '/signup', '/auth/email'].includes(location.pathname)
    ? new URLSearchParams(location.search).get('next') : location.pathname + location.search);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    const closeOnEscape = event => { if (event.key === 'Escape') setMobileMenuOpen(false); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, []);

  const isActive = (path) => location.pathname === path;

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleLogout = async () => {
    try { await logout(); closeMobileMenu(); }
    catch { toast.error('Sign-out could not be completed. Please try again.'); }
  };

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/70 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 font-extrabold text-xl text-white hover:text-indigo-400 transition-colors"
            data-testid="navbar-logo"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            onClick={closeMobileMenu}
          >
            <Activity className="w-6 h-6 text-indigo-500" />
            <span className="text-base sm:text-xl">PharmaConsensus</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/events"
              className={`text-sm font-medium transition-colors ${
                isActive('/events')
                  ? 'text-indigo-400'
                  : 'text-slate-300 hover:text-white'
              }`}
              data-testid="navbar-events-link"
            >
              Events
            </Link>

            {isAuthenticated && (
              <>
                <Link
                  to="/vote"
                  className={`text-sm font-medium transition-colors flex items-center gap-1 ${
                    isActive('/vote')
                      ? 'text-indigo-400'
                      : 'text-slate-300 hover:text-white'
                  }`}
                  data-testid="navbar-vote-link"
                >
                  <Flame className="w-4 h-4" />
                  Vote
                </Link>
                <Link
                  to="/profile"
                  className={`text-sm font-medium transition-colors ${
                    isActive('/profile')
                      ? 'text-indigo-400'
                      : 'text-slate-300 hover:text-white'
                  }`}
                  data-testid="navbar-profile-link"
                >
                  My Profile
                </Link>
                <Link
                  to="/my-votes"
                  className={`text-sm font-medium transition-colors ${
                    isActive('/my-votes')
                      ? 'text-indigo-400'
                      : 'text-slate-300 hover:text-white'
                  }`}
                  data-testid="navbar-my-votes-link"
                >
                  My Votes
                </Link>
                <Link
                  to="/suggest"
                  className={`text-sm font-medium transition-colors ${
                    isActive('/suggest')
                      ? 'text-indigo-400'
                      : 'text-slate-300 hover:text-white'
                  }`}
                  data-testid="navbar-suggest-link"
                >
                  Suggest
                </Link>
              </>
            )}

            {isAdmin && (
              <Link
                to="/admin"
                className={`text-sm font-medium transition-colors ${
                  location.pathname.startsWith('/admin')
                    ? 'text-indigo-400'
                    : 'text-slate-300 hover:text-white'
                }`}
                data-testid="navbar-admin-link"
              >
                <Settings className="w-4 h-4 inline" /> Admin
              </Link>
            )}

            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-400" data-testid="navbar-user-name">
                  {user?.name}
                </span>
                <button
                  onClick={handleLogout}
                  className="text-sm text-slate-300 hover:text-white transition-colors"
                  data-testid="navbar-logout-button"
                  aria-label="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to={loginDestination}
                  className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
                  data-testid="navbar-login-link"
                >
                  Sign In
                </Link>
                <Link
                  to={loginDestination}
                  className="text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-full transition-all hover:scale-105"
                  data-testid="navbar-signup-link"
                  style={{ boxShadow: '0 0 20px rgba(99,102,241,0.3)' }}
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-3">
            {isAuthenticated && (
              <span className="hidden sm:inline text-xs text-slate-400 max-w-[100px] truncate">
                {user?.name}
              </span>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="min-h-11 min-w-11 p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              aria-label={mobileMenuOpen ? 'Close navigation' : 'Open navigation'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div id="mobile-navigation" className="md:hidden border-t border-slate-800 bg-slate-950/95 backdrop-blur-xl">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/events"
              onClick={closeMobileMenu}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive('/events')
                  ? 'bg-indigo-500/10 text-indigo-400'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <TrendingUp className="w-5 h-5" />
              Browse Events
            </Link>

            {isAuthenticated && (
              <>
                <Link
                  to="/vote"
                  onClick={closeMobileMenu}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive('/vote')
                      ? 'bg-indigo-500/10 text-indigo-400'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Flame className="w-5 h-5" />
                  Quick Vote
                </Link>
                <Link
                  to="/profile"
                  onClick={closeMobileMenu}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive('/profile')
                      ? 'bg-indigo-500/10 text-indigo-400'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <User className="w-5 h-5" />
                  My Profile
                </Link>
                <Link
                  to="/my-votes"
                  onClick={closeMobileMenu}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive('/my-votes')
                      ? 'bg-indigo-500/10 text-indigo-400'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Vote className="w-5 h-5" />
                  My Votes
                </Link>
                <Link
                  to="/suggest"
                  onClick={closeMobileMenu}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive('/suggest')
                      ? 'bg-indigo-500/10 text-indigo-400'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Plus className="w-5 h-5" />
                  Suggest Event
                </Link>
              </>
            )}

            {isAdmin && (
              <Link
                to="/admin"
                onClick={closeMobileMenu}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname.startsWith('/admin')
                    ? 'bg-indigo-500/10 text-indigo-400'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Settings className="w-5 h-5" />
                Admin Dashboard
              </Link>
            )}

            <Link
              to="/support"
              onClick={closeMobileMenu}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive('/support')
                  ? 'bg-indigo-500/10 text-indigo-400'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <HelpCircle className="w-5 h-5" />
              Support
            </Link>

            <div className="border-t border-slate-800 mt-4 pt-4">
              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-rose-400 hover:bg-rose-500/10 transition-colors w-full"
                >
                  <LogOut className="w-5 h-5" />
                  Sign Out
                </button>
              ) : (
                <div className="space-y-2">
                  <Link
                    to={loginDestination}
                    onClick={closeMobileMenu}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link
                    to={loginDestination}
                    onClick={closeMobileMenu}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors"
                  >
                    Sign Up Free
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export const Footer = () => {
  return (
    <footer className="mt-10 border-t border-slate-800 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} PharmaConsensus. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <Link
                to="/support"
                className="text-sm text-slate-400 hover:text-indigo-400 transition-colors flex items-center gap-1"
              >
                <HelpCircle className="w-4 h-4" />
                Support
              </Link>
              <Link
                to="/terms"
                className="text-sm text-slate-400 hover:text-indigo-400 transition-colors"
              >
                Terms
              </Link>
              <Link
                to="/privacy"
                className="text-sm text-slate-400 hover:text-indigo-400 transition-colors"
              >
                Privacy
              </Link>
            </div>
          </div>
          <p className="text-xs text-slate-300 max-w-xl text-center md:text-right">
            This application is for entertainment and educational purposes only and does not involve
            betting, trading, or financial rewards.
          </p>
        </div>
      </div>
    </footer>
  );
};
